import { CalendarDays } from 'lucide-react';
import { useState } from 'react';
import type { Matcher } from 'react-day-picker';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  closureMessageFor,
  type ExperienceClosuresType,
} from '@/types/types_closure';
import { formatCalendarDate, parseCalendarDate, toCalendarDate } from '@/util/util_format';

interface ClosureDatePickerProps {
  /** `yyyy-mm-dd`, or '' for nothing chosen yet. */
  value: string;
  onChange: (value: string) => void;
  closures: ExperienceClosuresType;
  /** The earliest date that may be chosen, `yyyy-mm-dd` — usually today. */
  earliestDate: string;
  /** Names this particular date to a screen reader ("First choice of date"). */
  ariaLabel: string;
  placeholder?: string;
}

/**
 * A date field that will not let a closed day be chosen.
 *
 * The reason this is not `<input type='date'>` any more: a native date input has
 * no way to say that a day is unavailable, so a concierge could only find out by
 * submitting and being refused — with a guest waiting at the desk. Here the days
 * the artisan is away are struck through and unclickable, and the closures are
 * listed under the grid so the pattern ("closed every Monday") is legible rather
 * than something to be discovered a click at a time.
 *
 * Closed days are `disabled` *and* carry the `closed` modifier. Disabling alone
 * would render them like any past date, which says "not possible" without saying
 * why — and why is the part the concierge can act on.
 */
export function ClosureDatePicker({
  value,
  onChange,
  closures,
  earliestDate,
  ariaLabel,
  placeholder = 'Choose a date',
}: ClosureDatePickerProps) {
  const [open, setOpen] = useState(false);

  const selected = parseCalendarDate(value);
  const earliest = parseCalendarDate(earliestDate);
  const closedDays = closureMatchers(closures);
  const disabled: Matcher[] = earliest ? [{ before: earliest }, ...closedDays] : closedDays;

  // Only when the stored value is itself closed, which the picker cannot produce
  // but a closure added after the date was chosen certainly can.
  const closedNotice = closureMessageFor(value, closures);

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type='button'
            aria-label={ariaLabel}
            className='ed-input-elegant tw:flex tw:items-center tw:justify-between tw:gap-3 tw:text-left'
          >
            <span className={value ? 'tw:text-ed-black' : 'tw:text-ed-stone'}>
              {value ? formatCalendarDate(value) : placeholder}
            </span>
            <CalendarDays className='tw:size-4 tw:shrink-0 tw:text-ed-stone' />
          </button>
        </PopoverTrigger>
        <PopoverContent className='tw:max-w-[min(20rem,calc(100vw-2rem))]'>
          <Calendar
            mode='single'
            selected={selected}
            defaultMonth={selected ?? earliest}
            startMonth={earliest}
            disabled={disabled}
            modifiers={{ closed: closedDays }}
            modifiersClassNames={{ closed: 'ed-day-closed' }}
            onSelect={(date) => {
              if (!date) return;
              onChange(toCalendarDate(date));
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>

      {closedNotice && <p className='tw:mt-1.5 tw:text-xs tw:text-ed-red'>{closedNotice}</p>}
    </div>
  );
}

/**
 * The closures as day-picker matchers.
 *
 * Both kinds map straight onto matchers the library already understands — a span
 * is a range, a weekly closure is a `dayOfWeek` — so the calendar and
 * `findClosure` are reading the same two lists rather than two derived copies.
 * A period whose dates cannot be parsed is dropped: a matcher built on an
 * Invalid Date matches nothing anyway, and would take the whole grid with it.
 */
function closureMatchers(closures: ExperienceClosuresType): Matcher[] {
  const periods = closures.closurePeriods.flatMap((period) => {
    const from = parseCalendarDate(period.startDate);
    const to = parseCalendarDate(period.endDate);
    return from && to ? [{ from, to }] : [];
  });
  const weekdays = closures.weekdayClosures.map((closure) => closure.weekday);
  return weekdays.length > 0 ? [...periods, { dayOfWeek: weekdays }] : periods;
}
